import { ChatConfig, Message, StreamChunk } from '../types';

export interface ChatResponse {
  content: string;
  error?: string;
}

// 兼容旧配置格式的接口
export interface LegacyChatConfig {
  apiUrl: string;
  apiKey: string;
  model: string;
}

export class ChatService {
  private config: ChatConfig | LegacyChatConfig;
  private isLegacyConfig: boolean;

  constructor(config: ChatConfig | LegacyChatConfig) {
    this.config = config;
    this.isLegacyConfig = 'apiUrl' in config;
  }

  private getApiUrl(): string {
    if (this.isLegacyConfig) {
      return (this.config as LegacyChatConfig).apiUrl;
    }
    return (this.config as ChatConfig).baseUrl;
  }

  private getApiKey(): string {
    if (this.isLegacyConfig) {
      return (this.config as LegacyChatConfig).apiKey;
    }
    return (this.config as ChatConfig).apiKey;
  }

  private getModel(): string {
    if (this.isLegacyConfig) {
      return (this.config as LegacyChatConfig).model;
    }
    return (this.config as ChatConfig).currentModel;
  }

  async sendMessage(messages: Message[]): Promise<ChatResponse> {
    try {
      const apiUrl = this.getApiUrl();
      const apiKey = this.getApiKey();
      const model = this.getModel();

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${apiKey}`,
        },
        body: JSON.stringify({
          model: model,
          messages: messages.map(msg => ({
            role: msg.role,
            content: msg.content
          })),
          temperature: 0.7,
          max_tokens: 2000,
          stream: false
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error?.message || `HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      
      if (data.choices && data.choices[0] && data.choices[0].message) {
        return {
          content: data.choices[0].message.content
        };
      } else {
        throw new Error('Invalid response format from API');
      }
    } catch (error) {
      console.error('Chat API error:', error);
      return {
        content: '',
        error: error instanceof Error ? error.message : '未知错误'
      };
    }
  }

  async sendMessageStream(
    messages: Message[], 
    onChunk: (chunk: StreamChunk) => void
  ): Promise<void> {
    try {
      const apiUrl = this.getApiUrl();
      const apiKey = this.getApiKey();
      const model = this.getModel();

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${apiKey}`,
        },
        body: JSON.stringify({
          model: model,
          messages: messages.map(msg => ({
            role: msg.role,
            content: msg.content
          })),
          temperature: 0.7,
          max_tokens: 2000,
          stream: true
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error?.message || `HTTP ${response.status}: ${response.statusText}`);
      }

      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('无法读取响应流');
      }

      const decoder = new TextDecoder();
      let buffer = '';

      try {
        while (true) {
          const { done, value } = await reader.read();
          
          if (done) {
            // 处理剩余的buffer
            if (buffer.trim()) {
              const lines = buffer.split('\n');
              for (const line of lines) {
                if (line.startsWith('data: ')) {
                  const data = line.slice(6);
                  if (data === '[DONE]') {
                    onChunk({ content: '', done: true });
                    return;
                  }
                  try {
                    const parsed = JSON.parse(data);
                    if (parsed.choices && parsed.choices[0] && parsed.choices[0].delta) {
                      const content = parsed.choices[0].delta.content || '';
                      onChunk({ content, done: false });
                    }
                  } catch (e) {
                    // 忽略解析错误
                  }
                }
              }
            }
            onChunk({ content: '', done: true });
            return;
          }

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              const data = line.slice(6);
              if (data === '[DONE]') {
                onChunk({ content: '', done: true });
                return;
              }
              try {
                const parsed = JSON.parse(data);
                if (parsed.choices && parsed.choices[0] && parsed.choices[0].delta) {
                  const content = parsed.choices[0].delta.content || '';
                  onChunk({ content, done: false });
                }
              } catch (e) {
                // 忽略解析错误
              }
            }
          }
        }
      } finally {
        reader.releaseLock();
      }
    } catch (error) {
      console.error('Chat stream error:', error);
      onChunk({ 
        content: '', 
        done: true, 
        error: error instanceof Error ? error.message : '未知错误' 
      });
    }
  }

  updateConfig(newConfig: ChatConfig | LegacyChatConfig) {
    this.config = newConfig;
    this.isLegacyConfig = 'apiUrl' in newConfig;
  }

  validateConfig(): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    const apiUrl = this.getApiUrl();
    const apiKey = this.getApiKey();
    const model = this.getModel();

    if (!apiUrl.trim()) {
      errors.push('API URL 不能为空');
    } else if (!this.isValidUrl(apiUrl)) {
      errors.push('API URL 格式不正确');
    }

    if (!apiKey.trim()) {
      errors.push('API Key 不能为空');
    }

    if (!model.trim()) {
      errors.push('模型名称不能为空');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  private isValidUrl(string: string): boolean {
    try {
      new URL(string);
      return true;
    } catch (_) {
      return false;
    }
  }
} 